import {
  FileText,
  FolderOpen,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Trash2,
  Upload,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";

import { ApiError, deleteDocument, listDocuments, rereadSource } from "@/api/client";
import { useCan } from "@/auth/AuthProvider";
import type { DocumentListItem } from "@/api/client";
import { AddSourceDialog } from "@/screens/knowledge/AddSourceDialog";
import { READ_KINDS, kindLabel } from "@/screens/knowledge/kinds";
import { taughtLine, useExtraction } from "@/screens/knowledge/useExtraction";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Dialog } from "@/ui/Dialog";
import { EmptyState } from "@/ui/EmptyState";
import { Menu, MenuItem } from "@/ui/Menu";
import { Notice } from "@/ui/Notice";
import { Skeleton } from "@/ui/primitives";
import { Table, Td, Th } from "@/ui/Table";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * The organization's past work: take-offs, code standards, specs and exports
 * (ADR-0014). A past take-off is read and learned from as soon as it arrives.
 */
export function SourcesTab() {
  const [rows, setRows] = useState<DocumentListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const navigate = useNavigate();
  const [removing, setRemoving] = useState<DocumentListItem | null>(null);
  const [busy, setBusy] = useState(false);
  const canManage = useCan("knowledge:manage");

  const load = useCallback(async () => {
    try {
      const page = await listDocuments({ kind: "reference", limit: 200 });
      setRows(page.documents);
      setError(null);
    } catch (err) {
      setRows((now) => now ?? []);
      setError(err instanceof ApiError ? err.detail : "Could not reach the server.");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const remove = async () => {
    if (!removing) return;
    setBusy(true);
    try {
      await deleteDocument(removing.id);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "Could not remove the source.");
    } finally {
      setRemoving(null);
      setBusy(false);
    }
  };

  const add = canManage ? (
    <Button variant="primary" onClick={() => setAdding(true)}>
      <Plus aria-hidden="true" />
      Add source
    </Button>
  ) : null;

  return (
    <section aria-label="Sources">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-2xl text-sm text-ink-2">
          Past take-offs, your code standard and the documents behind them. What they hold is read
          and learned, so a new set starts from how your estimators already work.
        </p>
        {rows && rows.length > 0 ? add : null}
      </div>

      {error ? (
        <Notice tone="danger" className="mt-4">
          {error}
        </Notice>
      ) : null}

      <div className="mt-4">
        {rows === null ? (
          <div className="space-y-2" role="status" aria-label="Loading sources">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <EmptyState icon={<Upload />} title="No past work yet" action={add}>
            Add a finished take-off with the drawings it was done on, and your code legend. The more
            jobs it reads, the more a new set starts already named.
          </EmptyState>
        ) : (
          <Table aria-label="Sources">
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>What it is</Th>
                <Th className="hidden md:table-cell">What it taught</Th>
                <Th className="hidden text-right sm:table-cell">Pages</Th>
                <Th className="hidden lg:table-cell">Added</Th>
                <Th className="w-10">
                  <span className="sr-only">Actions</span>
                </Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="hover:bg-sunken/50">
                  <Td className="w-full max-w-0">
                    <Link
                      to={`/knowledge/sources/${row.id}`}
                      className="flex min-w-0 items-center gap-2.5 font-medium text-ink no-underline hover:text-accent"
                      title={row.filename}
                    >
                      <FileText aria-hidden="true" className="size-4 shrink-0 text-muted" />
                      <span className="truncate">{row.filename}</span>
                    </Link>
                  </Td>
                  <Td className="whitespace-nowrap">
                    <Badge tone="violet">{kindLabel(row.kind)}</Badge>
                  </Td>
                  <Td className="hidden whitespace-nowrap text-ink-2 md:table-cell">
                    {READ_KINDS.has(row.kind) ? <Taught id={row.id} /> : "Kept for reference"}
                  </Td>
                  <Td className="tabular hidden text-right text-ink-2 sm:table-cell">
                    {row.page_count ?? "…"}
                  </Td>
                  <Td className="hidden whitespace-nowrap text-ink-2 lg:table-cell">
                    {formatDate(row.created_at)}
                  </Td>
                  <Td className="text-right">
                    <Menu
                      trigger={
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-7 px-0"
                          aria-label={`Actions for ${row.filename}`}
                        >
                          <MoreHorizontal aria-hidden="true" />
                        </Button>
                      }
                    >
                      <MenuItem onSelect={() => void navigate(`/knowledge/sources/${row.id}`)}>
                        <FolderOpen aria-hidden="true" />
                        Open
                      </MenuItem>
                      {canManage && READ_KINDS.has(row.kind) ? (
                        <MenuItem onSelect={() => void rereadSource(row.id).then(load)}>
                          <RefreshCw aria-hidden="true" />
                          Read again
                        </MenuItem>
                      ) : null}
                      {canManage ? (
                        <MenuItem tone="danger" onSelect={() => setRemoving(row)}>
                          <Trash2 aria-hidden="true" />
                          Remove…
                        </MenuItem>
                      ) : null}
                    </Menu>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>

      <AddSourceDialog open={adding} onOpenChange={setAdding} onAdded={() => void load()} />
      <Dialog
        open={removing !== null}
        onOpenChange={(open) => {
          if (!open) setRemoving(null);
        }}
        label="Remove this source?"
      >
        <div className="space-y-4 p-4 text-sm text-ink-2">
          <p>
            <strong className="text-ink">{removing?.filename}</strong> will be removed, and the
            library unlearns what it taught. Names your estimators gave stay.
          </p>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setRemoving(null)}>Keep it</Button>
            <Button variant="danger" disabled={busy} onClick={() => void remove()}>
              Remove source
            </Button>
          </div>
        </div>
      </Dialog>
    </section>
  );
}

function Taught({ id }: { id: string }) {
  const { extraction, error } = useExtraction(id);
  return <span>{error ? "Could not be read" : taughtLine(extraction)}</span>;
}
